<template>
    <nav class="navbar navbar-expand-lg mr-nav-bar">
        
        <div class="navbar-brand d-flex">
            
            <slot name="prelogo">
                &nbsp;
            </slot>
            <router-link :to="uiStore.homePageLink" class="d-block mr-navbar-logo" aria-label="StayMarquis Home Page">
                <logo/>
            </router-link>
        </div>
        
        <template v-if="showMenuItems && !uiStore.checkOutFlow">
            <div v-if="uiStore.isMobile || uiStore.isTablet">
                <notifications-menu-button v-if="authStore.loggedInUser"/>
                <ui-button color="white" class="d-inline-flex align-items-center" @click="showMobileMenu">
                    <menu-icon :size="20"/>
                </ui-button>
            </div>
        
            <peek :open="mobileMenuOpen" v-if="uiStore.isMobile || uiStore.isTablet" @close="hideMobileMenu">
                <template #header>
                    <div class="d-flex align-items-center">
                        <span>Main Menu</span>
                    </div>
                </template>
                <template #headerButton v-if="!uiStore.isMobile && !authStore.loggedInUser">
                    <ui-button color="dark" :outline="true" @click="openLoginForm" class="ms-auto me-4">
                        Sign Up / Login
                    </ui-button>
                </template>
                <template #default>
                    <div @click="hideMobileMenuClick">
                        <slot name="default"/>
                    </div>
                    <template v-if="injectSideMenuMobile">
                        <hr v-if="startMenuContainer && $slots.default"/>
                        <div ref="startMenu" v-show="startMenuContainer" @click="hideMobileMenuClick"></div>
                        <template v-if="authStore.loggedInUser">
                            <hr/>
                            <div @click="hideMobileMenuClick">
                                
                                <router-link class="py-2 px-3 d-block side-menu-link fw-normal" :to="profileLink">
                                    My Profile
                                </router-link>

                                <router-link to="/travelers" class="py-2 px-3 d-block side-menu-link fw-normal">
                                    Travelers Account
                                </router-link>

                                <router-link v-for="(portal, index) in portals" class="py-2 px-3  d-block side-menu-link fw-normal" :key="`users-mobile-nav-portal-${index}`" :to="portal.url">
                                    {{ portal.label }}
                                </router-link>

                                <router-link v-if="uiStore.homePageLink != '/'" to="/" class="py-2 px-3 d-block side-menu-link fw-normal">
                                    Home Page
                                </router-link>

                                <a href="#" class="py-2 px-3  d-block side-menu-link fw-normal" @click.prevent="logout">
                                    Logout
                                </a>
                            </div>
                        </template>
                    </template>
                </template>
            </peek>
            <div v-else class="d-flex flex-grow-1 align-items-center">
                <slot name="default"/>

                <div v-if="authStore.loggedInUser" :class="pushUserButtons ? 'ms-auto' : null">
                    <notifications-menu-button/>
                    <drop-down :showArrow="false" :endAlign="true">
                        <template #toggle>
                            <ui-button color="white" aria-label="My Account" class="d-inline-flex align-items-center">
                                <profile-icon :size="20"/>
                            </ui-button>
                        </template>
                        <template #default>
                            
                            <drop-down-link :to="profileLink" class="pt-1">
                                Profile
                            </drop-down-link>

                            <drop-down-link to="/travelers">
                                Travelers Account
                            </drop-down-link>
                            <drop-down-link v-for="(portal, index) in portals" :key="`users-nav-portal-${index}`" :to="portal.url">
                                {{ portal.label }}
                            </drop-down-link>

                            <drop-down-link v-if="uiStore.homePageLink != '/'" to="/">
                                Home Page
                            </drop-down-link>

                            <drop-down-divider/>
                            <drop-down-link @click="logout" class="mb-2">
                                Logout
                            </drop-down-link>
                        </template>
                    </drop-down>
                </div>
                <div v-else :class="pushUserButtons ? 'ms-auto' : null">
                    <ui-button color="dark" :outline="true" @click="openLoginForm" class="me-4">
                        Sign Up / Login
                    </ui-button>
                </div>

                <slot name="endMenu"/>
            </div>
        </template>
        <login-signup-modal v-if="showLoginForm" :open="loginFormOpen" @close="loginFormClosed" @userAccount="goToAccount" />
    </nav>
    
</template>

<script type="text/javascript">
    import Logo from '@/components/Layout/Logo.vue';
    import UIComponents from '@/components/UI/index.vue';
    import NotificationsMenuButton from '@/components/Layout/Notifications/MenuButton.vue';
    import LoginSignupModal from '@/components/Content/Auth/LoginSignupModal.vue';
    import Peek from '@/components/Action/Peek.vue';
    import MenuIcon from '@/assets/icons/menu.svg.vue';
    import ProfileIcon from '@/assets/icons/profile.svg.vue';
    import UI from '@/classes/UI.js';
    import { UseUIStore } from '@/store/UI.js';
    import { UseAuthStore } from '@/store/Auth.js';

    export default {
        components: {
            Logo,
            Peek,
            NotificationsMenuButton,
            MenuIcon,
            ProfileIcon,
            LoginSignupModal,
            ...UIComponents,
        },

        setup() {
            return {
                uiStore: UseUIStore(),
                authStore: UseAuthStore(),
            };
        },

        data() {
            return {
                mobileMenuOpen: false,
                startMenuContainer: null,
                startMenuParentNode: null,
                injectSideMenuMobile: UI.injectSideMenuInMobile(),

                showLoginForm: false,
                loginFormOpen: true,
            };
        },

        props: {
            showMenuItems: {
                type: Boolean,
                default: true,
            },

            pushUserButtons: {
                type: Boolean,
                default: true,
            },

            loginForm: {
                type: Boolean,
                default: false,
            },
        },

        emits: ['loginFormOpen', 'loginFormClose'],

        created() {
            if (this.loginForm) {
                this.openLoginForm();
            }
        },

        watch: {
            $route() {
                this.hideMobileMenu();
            },

            loginForm(newVal) {
                if (newVal) {
                    this.openLoginForm();
                }
            },
        },

        computed: {

            portals() {
                let portals = [];
                if (
                    (this.authStore.loggedInUser) &&
                    (this.authStore.loggedInUser.portalsData) &&
                    (this.authStore.loggedInUser.portalsData.length)
                ) {
                    for (let i=0; i<this.authStore.loggedInUser.portalsData.length; i++) {
                        if (
                            (!this.authStore.loggedInUser.portalsData[i].usersApp) ||
                            (this.authStore.loggedInUser.portalsData[i].name == this.uiStore.appPortal)
                        ) {
                            continue;
                        }

                        portals.push(this.authStore.loggedInUser.portalsData[i]);
                    }
                }

                return portals;
            },

            profileLink() {
                if ((this.uiStore.homePageLink) && (this.uiStore.homePageLink != '/')) {
                    return `${this.uiStore.homePageLink}/profile`;
                }
                
                return '/travelers/profile';
            },
        },

        methods: {

            async logout() {
                let loggedOut = await this.authStore.logout();
                if (loggedOut) {
                    this.$router.push('/');
                }
            },

            showMobileMenu() {
                this.mobileMenuOpen = true;
                if (this.injectSideMenuMobile) {
                    requestAnimationFrame(function() {
                        this.startMenuContainer = document.querySelector('#app-mobile-start-menu-container');
                        if (this.startMenuContainer) {
                            this.startMenuParentNode = this.startMenuContainer.parentNode;
                            this.$refs.startMenu.appendChild(this.startMenuContainer);
                        }
                    }.bind(this));
                }
            },

            hideMobileMenuClick(evt) {
                // check if we clicked on a link
                let lnk = evt.target.closest('a');
                if (lnk) {
                    this.mobileMenuOpen = false;
                }
            },

            hideMobileMenu() {
                if (this.startMenuParentNode) {
                    this.startMenuParentNode.appendChild(this.startMenuContainer);
                }
                this.mobileMenuOpen = false;
            },

            openLoginForm() {
                this.loginFormOpen = true;
                this.showLoginForm = true;
                this.mobileMenuOpen = false;
                this.$emit('loginFormOpen');
            },

            loginFormClosed() {
                this.showLoginForm = false;
                this.loginFormOpen = true;
                this.$emit('loginFormClose');
            },

            goToAccount() {
                this.uiStore.toast({
                    success: true,
                    title: 'Redirecting to account...',
                });
                setTimeout(function() {
                    this.$router.push('/account');
                }.bind(this), 1000);
            },
        }
    }
</script>